// privacy.jsx - PyK7VM Privacy Policy
// Compiled to dist/privacy.js via build.mjs. Loaded by privacy.php.

const { useState, useEffect, useRef, useCallback } = React;
const { Nav, Footer, DiscordCTA } = window;

// ─── 12 POLICY SECTIONS ───────────────────────────────────────────────────────
const SECTIONS = [
  { id: 's-1',  label: '1. What we receive' },
  { id: 's-2',  label: '2. Why we process your data' },
  { id: 's-3',  label: '3. How we store your source code' },
  { id: 's-4',  label: '4. Access' },
  { id: 's-5',  label: '5. Malware and abuse' },
  { id: 's-6',  label: '6. Account data and cookies' },
  { id: 's-7',  label: '7. Service providers & transfers' },
  { id: 's-8',  label: '8. How long we keep data' },
  { id: 's-9',  label: '9. Your rights' },
  { id: 's-10', label: '10. Self-hosted option (roadmap)' },
  { id: 's-11', label: '11. Changes to this policy' },
  { id: 's-12', label: '12. Controller and contact' },
];

// ─── RETENTION MATRIX DATA ────────────────────────────────────────────────────
const RETENTION_SCHEDULE = [
  {
    category: 'Source Code & Input Files (.py, .zip)',
    liveRetention: 'Duration of build + download window',
    purgeTTL: '24 hours max (abandoned/failed builds auto-purged)',
    backupTTL: 'Rolling host DR snapshot (max 30 days)',
  },
  {
    category: 'Compiled Native Binaries (.pyd, .so)',
    liveRetention: 'Download window immediately following build',
    purgeTTL: '24 hours max post-completion',
    backupTTL: 'Rolling host DR snapshot (max 30 days)',
  },
  {
    category: 'Account Data & Password Hashes',
    liveRetention: 'Duration of active account',
    purgeTTL: 'Deleted within 30 days of account termination',
    backupTTL: 'Rolling host DR snapshot (max 30 days)',
  },
  {
    category: 'Build Job History Metadata',
    liveRetention: 'Duration of active account',
    purgeTTL: 'Deleted within 30 days of account termination',
    backupTTL: 'Rolling host DR snapshot (max 30 days)',
  },
  {
    category: 'Technical Server Logs',
    liveRetention: '12 months rolling window',
    purgeTTL: 'Deleted automatically after 12 months',
    backupTTL: 'Rolling host DR snapshot (max 30 days)',
  },
  {
    category: 'Malware & Abuse Records (Section 5)',
    liveRetention: 'Up to 12 months from detection',
    purgeTTL: 'Extended only during pending binding legal inquiry',
    backupTTL: 'Encrypted legal hold partition',
  },
];

// ─── REUSABLE TYPOGRAPHY & BLOCKS ─────────────────────────────────────────────

const h2 = { fontSize: 'clamp(18px, 2.5vw, 22px)', fontWeight: 600, letterSpacing: -0.4, color: 'var(--text)', marginBottom: 14, marginTop: 0, lineHeight: 1.3 };
const h3 = { fontSize: 'clamp(14.5px, 2vw, 16px)', fontWeight: 600, color: 'var(--text)', marginBottom: 10, marginTop: 24 };
const p  = { color: 'var(--text-dim)', lineHeight: 1.75, marginBottom: 14, fontSize: 'clamp(13.5px, 1.8vw, 14.5px)' };
const ul = { paddingLeft: 20, marginBottom: 14, color: 'var(--text-dim)', fontSize: 'clamp(13.5px, 1.8vw, 14.5px)', lineHeight: 1.75 };
const li = { marginBottom: 6 };
const strong = { color: 'var(--text)', fontWeight: 600 };
const code = { fontFamily: 'var(--font-code)', fontSize: 12, background: 'rgba(245,241,232,0.07)', padding: '2px 6px', borderRadius: 4, color: 'var(--brand-soft)' };
const mailBrand = { color: 'var(--brand-soft)', textDecoration: 'none', borderBottom: '1px solid rgba(59,130,246,0.3)' };

function Callout({ children }) {
  return (
    <div style={{ background: 'rgba(59,130,246,0.04)', border: '1px solid rgba(59,130,246,0.18)', borderLeft: '3px solid var(--brand)', padding: '14px 18px', borderRadius: '0 8px 8px 0', margin: '20px 0', color: 'var(--text-dim)', lineHeight: 1.7, fontSize: 13.5 }}>
      {children}
    </div>
  );
}

// ─── LIFECYCLE SUMMARY TABLE ──────────────────────────────────────────────────
function LifecycleSummaryTable() {
  const steps = [
    { num: 'Step 1', title: 'TLS 1.3 Ingestion', desc: 'Code streams over encrypted TLS 1.3 straight to an isolated RAM scratch directory on Alwaysdata Paris servers.' },
    { num: 'Step 2', title: 'Sandboxed Virtualization', desc: 'Python AST is parsed, mutated with randomized opcodes, and compiled to native C++ without cross-job memory sharing.' },
    { num: 'Step 3', title: 'Binary Download', desc: 'Protected .pyd / .so is cryptographically signed and streamed to your client over HTTPS.' },
    { num: 'Step 4', title: 'Permanent Purge', desc: 'All source files, AST trees, and binaries are unlinked from disk immediately upon download (or within 24h for failed/abandoned jobs).' },
  ];

  return (
    <div style={{ margin: '20px 0', border: '1px solid var(--border-strong)', borderRadius: 8, background: 'rgba(10,10,15,0.5)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', background: 'rgba(245,241,232,0.02)', borderBottom: '1px solid var(--border)', fontSize: 11.5, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--brand-soft)', fontFamily: 'var(--font-code)' }}>
        Source Code Ephemerality Lifecycle
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1px', background: 'var(--border)' }} className="life-grid">
        {steps.map((s) => (
          <div key={s.num} style={{ background: 'rgba(8,8,13,0.95)', padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-soft)', fontFamily: 'var(--font-code)', marginBottom: 4 }}>
              {s.num}: {s.title}
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.55, margin: 0 }}>
              {s.desc}
            </p>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 700px) { .life-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

// ─── RETENTION MATRIX TABLE ───────────────────────────────────────────────────
function RetentionTable() {
  return (
    <div style={{ margin: '20px 0', border: '1px solid var(--border-strong)', borderRadius: 8, background: 'rgba(10,10,15,0.5)', overflow: 'hidden' }}>
      <div className="table-responsive-wrapper table-sticky-col">
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 540 }}>
          <thead>
            <tr style={{ background: 'rgba(245,241,232,0.03)', borderBottom: '1px solid var(--border-strong)' }}>
              <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: 'var(--font-code)' }}>Data Category</th>
              <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: 'var(--font-code)' }}>Live Retention</th>
              <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: 'var(--font-code)' }}>Purge Timeout</th>
              <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: 'var(--font-code)' }}>DR Backups</th>
            </tr>
          </thead>
          <tbody>
            {RETENTION_SCHEDULE.map((r, i) => (
              <tr key={r.category} style={{ borderBottom: i < RETENTION_SCHEDULE.length - 1 ? '1px solid var(--border)' : 'none', background: i % 2 === 1 ? 'rgba(245,241,232,0.01)' : 'transparent' }}>
                <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{r.category}</td>
                <td style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--text-dim)' }}>{r.liveRetention}</td>
                <td style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--brand-soft)', fontWeight: 500 }}>{r.purgeTTL}</td>
                <td style={{ padding: '12px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{r.backupTTL}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── MAIN POLICY TEXT (SECTIONS 1 TO 12) ──────────────────────────────────────

function PrivacyContent() {
  return (
    <>
      <section id="s-1" style={{ marginBottom: 40, scrollMarginTop: 100 }}>
        <h2 style={h2}>1. What we receive</h2>
        <p style={p}>When you use PyK7VM you submit two kinds of data:</p>
        <ul style={ul}>
          <li style={li}><strong style={strong}>Account data:</strong> Email address, hashed password (Argon2id/bcrypt), subscription tier (Free, Beta, Premium), and API tokens.</li>
          <li style={li}><strong style={strong}>Source material:</strong> The Python files (<code style={code}>.py</code>) or archives (<code style={code}>.zip</code>) you upload for protection, plus compilation flags set in the Forge or CLI.</li>
        </ul>
        <p style={p}>In addition, like every web service, our servers automatically record <strong style={strong}>technical logs</strong> (IP address, request timestamps, and request metadata) when you visit the site or call the REST API.</p>
        <p style={p}>Providing an email address is required to create an account and manage API keys; without it we cannot provide the service. Everything else is provided only when you choose to use the corresponding feature.</p>
      </section>

      <section id="s-2" style={{ marginBottom: 40, scrollMarginTop: 100 }}>
        <h2 style={h2}>2. Why we process your data</h2>
        <p style={p}>Everything we do with your data has a specific functional purpose:</p>
        <ul style={ul}>
          <li style={li}><strong style={strong}>Account data (email, password hash):</strong> needed to create, authenticate, and run your account.</li>
          <li style={li}><strong style={strong}>Uploaded source code and build configuration:</strong> used exclusively to perform the compilation and virtualization you requested.</li>
          <li style={li}><strong style={strong}>Build history metadata (timestamps, file sizes, target Python version):</strong> kept so you can see past builds in the dashboard, and so we can secure the service and enforce plan quotas.</li>
          <li style={li}><strong style={strong}>Abuse and malware screening (section 5):</strong> we screen submitted builds to stop the service from being used to produce malware, to protect other users and third parties, and to defend ourselves legally.</li>
          <li style={li}><strong style={strong}>Technical server logs:</strong> kept for security, debugging, and abuse prevention.</li>
        </ul>
        <p style={p}>None of this relies on ambiguous consent. Data processing is grounded in contract performance and legitimate interests in protecting platform integrity.</p>
      </section>

      <section id="s-3" style={{ marginBottom: 40, scrollMarginTop: 100 }}>
        <h2 style={h2}>3. How we store your source code</h2>
        <p style={p}>Your source code is treated as ephemeral build input, not as stored content.</p>
        <ul style={ul}>
          <li style={li}>Uploads travel over TLS 1.3 and are written to an isolated job directory on our build infrastructure, located in France (European Union).</li>
          <li style={li}>Each build runs in a per-job workspace. Workspaces are not shared across users or jobs.</li>
          <li style={li}>Once the build finishes and the resulting <code style={code}>.pyd</code> is downloaded, the source files and build artefacts are deleted from disk.</li>
          <li style={li}>If a build fails or is abandoned, the workspace is purged automatically within 24 hours.</li>
        </ul>

        <LifecycleSummaryTable />

        <Callout>
          <strong style={strong}>Zero Model Training Guarantee.</strong> Source code lives on our systems only for the duration of the build and download window. It is not archived and is <strong style={strong}>NEVER used to train or evaluate AI/ML models</strong>. We do not create backups of your source code ourselves; our hosting provider's routine infrastructure backups may retain short-lived copies for up to 30 days before they roll off.
        </Callout>

        <h3 style={h3}>Personal data inside your uploads</h3>
        <p style={p}>Your source code may itself contain personal data, like author names in comments or test fixtures. That content stays your responsibility, and we handle it strictly on your behalf: we touch it only to perform the build you requested, under this policy and our Terms of Service, and we delete it as described above.</p>
      </section>

      <section id="s-4" style={{ marginBottom: 40, scrollMarginTop: 100 }}>
        <h2 style={h2}>4. Access</h2>
        <p style={p}>Access to the build infrastructure is limited to the project operator via hardened SSH keys and multi-factor authentication. We do not inspect customer source code as a matter of practice, and we do not share it with anyone except the service providers and circumstances described in this policy.</p>
      </section>

      <section id="s-5" style={{ marginBottom: 40, scrollMarginTop: 100 }}>
        <h2 style={h2}>5. Malware and abuse</h2>
        <p style={p}>PyK7VM is not a tool for malware authors. When automated or manual review identifies content that is plausibly malicious, abusive, or illegal, we reserve the right to:</p>
        <ul style={ul}>
          <li style={li}>Refuse the build and terminate the associated account.</li>
          <li style={li}>Retain a copy of the submitted material for up to 12 months from detection, extended only while a specific law-enforcement request, investigation, or legal claim concerning it is pending.</li>
        </ul>
        <p style={p}>This is the only scenario in which uploaded source code is kept beyond the normal build lifecycle. We disclose retained material only in response to requests that are valid and binding under European Union or French law.</p>
        <p style={p}>Automated screening may flag or pause a build, but no account is terminated and no material is retained under this section without manual review. If you believe a decision was made in error, contact <a href="mailto:privacy@pyk7vm.com" style={mailBrand}>privacy@pyk7vm.com</a> and a person will re-review it.</p>
      </section>

      <section id="s-6" style={{ marginBottom: 40, scrollMarginTop: 100 }}>
        <h2 style={h2}>6. Account data and cookies</h2>
        <p style={p}>We store the minimum required to run your account: email, password hash, and build history metadata (timestamps, file sizes, target Python version). We do not sell this data. We use first-party cookies only for session management; these are strictly necessary cookies and require no consent banner.</p>
        <p style={p}>All page assets and web fonts are served from our own domain, so visiting this site does not send your IP address to third-party font or analytics services.</p>
      </section>

      <section id="s-7" style={{ marginBottom: 40, scrollMarginTop: 100 }}>
        <h2 style={h2}>7. Service providers and international transfers</h2>
        <p style={p}>We use a single service provider, who processes data on our behalf under a data-processing agreement:</p>
        <ul style={ul}>
          <li style={li}><strong style={strong}>Alwaysdata SARL</strong> (91 rue du Faubourg Saint-Honoré, 75008 Paris, France), our web host. Production servers sit in datacenters in Paris, and daily backups are stored in a separate datacenter, also in the Paris region. Everything is hosted in the European Union, and Alwaysdata has its own Data Protection Officer.</li>
        </ul>
        <p style={p}><strong style={strong}>International transfers:</strong> All personal data is stored and processed within the European Union (France). We do not transfer personal data outside the EU.</p>
      </section>

      <section id="s-8" style={{ marginBottom: 40, scrollMarginTop: 100 }}>
        <h2 style={h2}>8. How long we keep data</h2>
        <p style={p}>We apply strict data minimization across all data classes:</p>
        
        <RetentionTable />

        <Callout>
          <strong style={strong}>Disaster recovery backups.</strong> Our host makes automatic daily backups kept on a rolling basis for at most 30 days. Data deleted from our live systems disappears from those backups automatically within at most 30 further days. Backups are used only for disaster recovery, never to restore deleted personal data.
        </Callout>
      </section>

      <section id="s-9" style={{ marginBottom: 40, scrollMarginTop: 100 }}>
        <h2 style={h2}>9. Your rights</h2>
        <p style={p}>You can ask us at any time to see the personal data we hold about you, fix it, get a copy of it in a portable format, delete it, freeze its processing, or object to the processing we do for our own legitimate interests (including abuse screening). These rights apply to every user, wherever you are located. Just write to <a href="mailto:privacy@pyk7vm.com" style={mailBrand}>privacy@pyk7vm.com</a>. We will respond within one month; for complex requests this can stretch by up to two further months, and we will tell you if that happens.</p>
        <p style={p}>You can also complain to a data-protection authority. Ours is the French CNIL (Commission Nationale de l'Informatique et des Libertés), <a href="https://www.cnil.fr" style={mailBrand} rel="noopener noreferrer" target="_blank">www.cnil.fr</a>, though you can just as well go to the authority of your own EU member state.</p>
        <p style={p}>Account deletion removes your login and build history within 30 days, after which copies in rolling host backups expire automatically. Source code uploaded during your use of the service has already been deleted as described in section 3, so no separate deletion step is required for it.</p>
      </section>

      <section id="s-10" style={{ marginBottom: 40, scrollMarginTop: 100 }}>
        <h2 style={h2}>10. Self-hosted option (roadmap)</h2>
        <p style={p}>A self-hosted build of the compiler is on the product roadmap for organisations that cannot send source code to a third party. In that configuration, your code would never leave your own infrastructure: no uploads, no build servers, no data sharing. This option is not yet available. Write to <a href="mailto:sales@pyk7vm.com" style={mailBrand}>sales@pyk7vm.com</a> to be notified when it launches.</p>
      </section>

      <section id="s-11" style={{ marginBottom: 40, scrollMarginTop: 100 }}>
        <h2 style={h2}>11. Changes to this policy</h2>
        <p style={p}>If we change how we handle data, we will update this page and record the change date at the top. Material changes will be announced in-product before they take effect.</p>
      </section>

      <section id="s-12" style={{ marginBottom: 0, scrollMarginTop: 100 }}>
        <h2 style={h2}>12. Controller and contact</h2>
        <p style={p}>PyK7VM is operated as a specialized project based in France, who is the data controller for all processing described in this policy. For privacy questions and any of the requests described in section 9, write to <a href="mailto:privacy@pyk7vm.com" style={mailBrand}>privacy@pyk7vm.com</a>.</p>
        <p style={p}>We do not have a Data Protection Officer; a service of this size is not required to appoint one, and the contact above handles all privacy matters.</p>
        <p style={p}>The service is hosted by Alwaysdata SARL, 91 rue du Faubourg Saint-Honoré, 75008 Paris, France.</p>
      </section>
    </>
  );
}

// ─── STICKY NUMBERED SIDEBAR ──────────────────────────────────────────────────

function Sidebar({ activeId, onSelect }) {
  const sidebarRef = useRef(null);
  const itemRefs = useRef({});

  // Auto-scroll sidebar so active item stays visible without hijacking window scroll
  useEffect(() => {
    const activeEl = itemRefs.current[activeId];
    const sidebarEl = sidebarRef.current;
    if (activeEl && sidebarEl) {
      const activeTop = activeEl.offsetTop;
      const activeHeight = activeEl.offsetHeight;
      const sidebarHeight = sidebarEl.clientHeight;
      const sidebarScroll = sidebarEl.scrollTop;

      if (activeTop < sidebarScroll + 30) {
        sidebarEl.scrollTo({ top: Math.max(0, activeTop - 30), behavior: 'smooth' });
      } else if (activeTop + activeHeight > sidebarScroll + sidebarHeight - 30) {
        sidebarEl.scrollTo({ top: activeTop + activeHeight - sidebarHeight + 30, behavior: 'smooth' });
      }
    }
  }, [activeId]);

  return (
    <aside
      ref={sidebarRef}
      className="privacy-sidebar custom-scrollbar"
      style={{
        position: 'sticky',
        top: 96,
        alignSelf: 'start',
        height: 'calc(100vh - 120px)',
        overflowY: 'auto',
        paddingRight: 16,
        borderRight: '1px solid var(--border)',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: 'var(--brand-soft)', textTransform: 'uppercase', marginBottom: 16, fontFamily: 'var(--font-code)' }}>
        On this page
      </div>
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {SECTIONS.map((s) => {
          const isActive = activeId === s.id;
          return (
            <a
              key={s.id}
              ref={(el) => (itemRefs.current[s.id] = el)}
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                onSelect(s.id);
              }}
              style={{
                display: 'block',
                padding: '7px 10px',
                fontSize: 13.5,
                lineHeight: 1.4,
                textDecoration: 'none',
                borderLeft: `2px solid ${isActive ? 'var(--brand)' : 'transparent'}`,
                background: isActive ? 'rgba(59,130,246,0.1)' : 'transparent',
                color: isActive ? '#ffffff' : 'var(--text-muted)',
                fontWeight: isActive ? 600 : 400,
                borderRadius: isActive ? '0 6px 6px 0' : 6,
                transition: 'color .2s, border-color .2s, background .2s',
                marginBottom: 2,
              }}
            >
              {s.label}
            </a>
          );
        })}
      </nav>
    </aside>
  );
}

// ─── MAIN PRIVACY APP WITH SMOOTH LENIS ────────────────────────────────────────

function PrivacyApp() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [mobileSelect, setMobileSelect] = useState(SECTIONS[0].id);
  const isClickScrolling = useRef(false);
  const clickLockTimeout = useRef(null);

  useEffect(() => {
    if (typeof window.Lenis !== 'undefined') {
      const lenis = new window.Lenis({
        duration: 0.8,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        touchMultiplier: 1.5,
      });
      window.__lenis = lenis;

      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);

      return () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      };
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (isClickScrolling.current) return;

    const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    const headerBuffer = 150;

    if (window.innerHeight + scrollY >= document.documentElement.scrollHeight - 80) {
      const lastId = SECTIONS[SECTIONS.length - 1].id;
      setActiveId(lastId);
      setMobileSelect(lastId);
      return;
    }

    let bestId = SECTIONS[0].id;
    for (let i = 0; i < SECTIONS.length; i++) {
      const id = SECTIONS[i].id;
      const el = document.getElementById(id);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= headerBuffer) {
          bestId = id;
        }
      }
    }

    setActiveId(bestId);
    setMobileSelect(bestId);
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    let timer = setInterval(() => {
      if (window.__lenis) {
        window.__lenis.on('scroll', handleScroll);
        clearInterval(timer);
      }
    }, 150);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (window.__lenis) window.__lenis.off('scroll', handleScroll);
      clearInterval(timer);
      if (clickLockTimeout.current) clearTimeout(clickLockTimeout.current);
    };
  }, [handleScroll]);

  const scrollTo = useCallback((id) => {
    setActiveId(id);
    setMobileSelect(id);

    isClickScrolling.current = true;
    if (clickLockTimeout.current) clearTimeout(clickLockTimeout.current);
    clickLockTimeout.current = setTimeout(() => {
      isClickScrolling.current = false;
    }, 900);

    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
    if (window.__lenis) {
      window.__lenis.scrollTo(y, { duration: 0.8 });
    } else {
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  const handleMobileChange = (e) => {
    const targetId = e.target.value;
    setMobileSelect(targetId);
    scrollTo(targetId);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <Nav />
      <div style={{ flex: 1, maxWidth: 1240, margin: '0 auto', width: '100%', padding: 'clamp(80px, 10vw, 120px) clamp(16px, 4vw, 32px) clamp(40px, 6vw, 100px)' }}>
        
        {/* Document Header */}
        <header style={{ marginBottom: 36, paddingBottom: 20, borderBottom: '1px solid var(--border)' }}>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 500, letterSpacing: -1.8, lineHeight: 1.06, marginBottom: 12, color: 'var(--text)' }}>
            Privacy Policy
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'clamp(14px, 2vw, 16px)', lineHeight: 1.6, maxWidth: 640, margin: 0 }}>
            How PyK7VM handles source code uploaded to the service: storage, retention, deletion, and GDPR compliance.
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: 12.5, marginTop: 8 }}>
            Last updated: August 23, 2026
          </p>
        </header>

        {/* Mobile Sticky Selector Bar */}
        <div
          className="mobile-toc-bar"
          style={{
            display: 'none',
            position: 'sticky',
            top: 72,
            zIndex: 40,
            background: 'rgba(8,8,13,0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid var(--border-strong)',
            borderRadius: 8,
            padding: '8px 12px',
            marginBottom: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-soft)', fontFamily: 'var(--font-code)', textTransform: 'uppercase' }}>
              Section:
            </span>
            <select
              value={mobileSelect}
              onChange={handleMobileChange}
              style={{
                flex: 1,
                background: 'rgba(10,10,15,0.95)',
                border: '1px solid var(--border)',
                color: 'var(--text)',
                padding: '9px 10px',
                borderRadius: 6,
                fontSize: 13,
                outline: 'none',
                fontFamily: 'var(--font-ui)',
                minHeight: 44,
              }}
            >
              {SECTIONS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Desktop 2-Column Grid */}
        <div
          className="privacy-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '260px 1fr',
            gap: 'clamp(24px, 4vw, 56px)',
            alignItems: 'start',
          }}
        >
          <Sidebar activeId={activeId} onSelect={scrollTo} />
          <main style={{ minWidth: 0 }}>
            <PrivacyContent />
          </main>
        </div>

      </div>

      <Footer />
      <DiscordCTA />

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(245, 241, 232, 0.12);
          border-radius: 999px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.6);
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(245, 241, 232, 0.15) transparent;
        }

        @media (max-width: 960px) {
          .privacy-sidebar { display: none !important; }
          .privacy-grid { grid-template-columns: 1fr !important; }
          .mobile-toc-bar { display: block !important; }
        }
        @media (min-width: 961px) {
          .privacy-sidebar { display: block !important; }
          .privacy-grid { grid-template-columns: 260px minmax(0, 1fr) !important; }
          .mobile-toc-bar { display: none !important; }
        }
      `}</style>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(PrivacyApp));
